import * as React from "react";
import { ArrowDown } from "lucide-react";

import { cn } from "../../lib/utils";

type ConversationContextValue = {
  atBottom: boolean;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  setAtBottom: (atBottom: boolean) => void;
};

const ConversationContext = React.createContext<ConversationContextValue | null>(null);

const useConversation = (): ConversationContextValue => {
  const context = React.useContext(ConversationContext);
  if (!context) {
    throw new Error("Conversation components must be used within Conversation");
  }
  return context;
};

const Conversation = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const [atBottom, setAtBottom] = React.useState(true);

    const scrollToBottom = React.useCallback((behavior: ScrollBehavior = "smooth") => {
      const content = contentRef.current;
      if (!content) return;

      content.scrollTo({ top: content.scrollHeight, behavior });
    }, []);

    const contextValue = React.useMemo(
      () => ({ atBottom, contentRef, scrollToBottom, setAtBottom }),
      [atBottom, scrollToBottom],
    );

    return (
      <ConversationContext.Provider value={contextValue}>
        <div ref={ref} className={cn("relative flex min-h-0 flex-1 flex-col", className)} {...props}>
          {children}
        </div>
      </ConversationContext.Provider>
    );
  },
);
Conversation.displayName = "Conversation";

const ConversationContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const { atBottom, contentRef, scrollToBottom, setAtBottom } = useConversation();
    const innerRef = React.useRef<HTMLDivElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node;

        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [contentRef, ref],
    );

    const updateAtBottom = React.useCallback(() => {
      const content = contentRef.current;
      if (!content) return;

      const distanceFromBottom = content.scrollHeight - content.scrollTop - content.clientHeight;
      setAtBottom(distanceFromBottom < 48);
    }, [contentRef, setAtBottom]);

    React.useEffect(() => {
      if (!atBottom) return;

      const frame = window.requestAnimationFrame(() => scrollToBottom("smooth"));
      return () => window.cancelAnimationFrame(frame);
    }, [atBottom, children, scrollToBottom]);

    React.useEffect(() => {
      const inner = innerRef.current;
      if (!inner) return;

      const resizeObserver = new ResizeObserver(() => {
        updateAtBottom();
        if (atBottom) scrollToBottom("smooth");
      });

      resizeObserver.observe(inner);
      return () => resizeObserver.disconnect();
    }, [atBottom, scrollToBottom, updateAtBottom]);

    return (
      <div ref={setRefs} className="min-h-0 flex-1 overflow-y-auto scroll-smooth" onScroll={updateAtBottom} {...props}>
        <div ref={innerRef} className={cn("flex min-h-full flex-col gap-4 p-4", className)}>
          {children}
        </div>
      </div>
    );
  },
);
ConversationContent.displayName = "ConversationContent";

type ConversationEmptyStateProps = React.HTMLAttributes<HTMLDivElement> & {
  description?: React.ReactNode;
  icon?: React.ReactNode;
  title?: React.ReactNode;
};

const ConversationEmptyState = React.forwardRef<HTMLDivElement, ConversationEmptyStateProps>(
  ({ children, className, description, icon, title, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex h-full min-h-64 flex-col items-center justify-center p-6 text-center text-sm text-gray-500",
        className,
      )}
      {...props}
    >
      {icon ? <div className="mb-3 text-gray-400">{icon}</div> : null}
      {title ? <div className="mb-1 font-medium text-gray-700">{title}</div> : null}
      {description ?? children}
    </div>
  ),
);
ConversationEmptyState.displayName = "ConversationEmptyState";

const ConversationScrollButton = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, children, ...props }, ref) => {
    const { atBottom, scrollToBottom } = useConversation();

    if (atBottom) return null;

    return (
      <button
        ref={ref}
        type="button"
        aria-label="Przewiń do najnowszej wiadomości"
        className={cn(
          "absolute bottom-4 right-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-md transition-colors hover:bg-gray-50 hover:text-gray-950",
          className,
        )}
        onClick={() => scrollToBottom("smooth")}
        {...props}
      >
        {children ?? <ArrowDown className="h-4 w-4" />}
      </button>
    );
  },
);
ConversationScrollButton.displayName = "ConversationScrollButton";

export { Conversation, ConversationContent, ConversationEmptyState, ConversationScrollButton, useConversation };
